/* eslint-disable react-hooks/exhaustive-deps */
import { Button } from '@/components/button'
import { ChatModal } from '@/components/chat/ChatModal'
import { Matches, Statistical } from '@/components/common'
import { MainContent } from '@/components/content'
import { ArrowBack } from '@/components/icons'
import { MainLayout } from '@/components/layouts'
import { SponsoredModal } from '@/components/modal/SponsoredModal'
import { Profile } from '@/components/profile'
import { ProfileLimitBanner, ProfileUnlockGate } from '@/components/profile-limit'
import { MainSidebar, MobileSidebar } from '@/components/sidebar'
import { ProfileLoading } from '@/components/skeleton'
import {
  LIMIT,
  OFFSET,
  SCROLLING_HEIGHT_ABS_ERROR,
  SCROLLING_THROTTLING_DURATION,
  SORT_ORDER,
} from '@/constants/Common'
import { useDebounce } from '@/hooks/useDebounce'
import { useProfileViews, useProfileViewsLimited } from '@/hooks/useProfileViews'
import { useAppDispatch, useAppSelector } from '@/hooks/useRedux'
import { authSelector } from '@/pages/auth/selectors'
import { appSelector } from '@/pages/home/selectors'
import { userSelector } from '@/pages/user/selectors'
import { setEmptyChannelList, setPartnerChatIDState, setVerifyEmailModalState } from '@/pages/user/userSlice'
import { useGetFollowingInfo, useGetMatchesHistory, useGetPlayerProfile } from '@/services'
import { EventFormat, Match } from '@/types'
import { throttle } from 'lodash'
import moment from 'moment'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'

export const PlayerProfile = () => {
  const { t } = useTranslation()

  const params = useParams()
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const { isShowDropdown } = useAppSelector(appSelector)
  const { user } = useAppSelector(userSelector)
  const { token } = useAppSelector(authSelector)

  const { playerId } = params

  const { data: player } = useGetPlayerProfile(playerId)
  const { mutate: getMatches, isLoading: isLoadingMatches, data: resMatchesHistory } = useGetMatchesHistory()
  const { data: resFollowingInfo } = useGetFollowingInfo(player?.id, token)

  // The views-limit state owns the access decision: a limited viewer owes an
  // unlock for any other profile they haven't already unlocked this window.
  const profileLimited = useProfileViewsLimited()
  const { viewedIds, viewsLimit, recordView } = useProfileViews()
  const isOwnProfile = !!user && !!player && user.id === player.id
  const amongAllotted = !!player && viewedIds.slice(0, viewsLimit).includes(player.id)
  const requiresUnlock = !!user && !!player && profileLimited && !isOwnProfile && !amongAllotted
  const showLimitBanner = !!user && !!player && profileLimited && !isOwnProfile && !requiresUnlock

  const [matches, setMatches] = useState<Match[]>([])
  const [offset, setOffset] = useState(OFFSET)
  const [openChat, setOpenChat] = useState(false)
  const [eventFormat, setEventFormat] = useState<EventFormat | null>(null)
  const [openSponsored, setOpenSponsored] = useState(false)
  const [sortParameter, setSortParameter] = useState<'CREATED_DATE' | 'MODIFIED_DATE' | 'MATCH_DATE'>('MATCH_DATE')
  const [startDate, setStartDate] = useState<Date | null>(null)
  const [endDate, setEndDate] = useState<Date | null>(null)
  const [searchEventName, setSearchEventName] = useState('')

  const debouncedEventName = useDebounce<string>(searchEventName || '', 200)

  const closeModal = () => {
    setOpenChat(false)
  }

  const toggleSponsoredModal = () => setOpenSponsored(!openSponsored)

  const onGetMatchesHistory = () => {
    getMatches({
      userId: Number(playerId),
      filters: {
        eventFormat: eventFormat ? [eventFormat] : null,
        ...(debouncedEventName && { eventName: debouncedEventName }),
        ...(startDate &&
          endDate && {
            eventDate: {
              startDate: moment(startDate).format('YYYY-MM-DD'),
              endDate: moment(endDate).format('YYYY-MM-DD'),
            },
          }),
      },
      limit: LIMIT,
      offset: offset,
      sort: {
        order: SORT_ORDER,
        parameter: sortParameter,
      },
    })
  }

  const onGoBack = () => navigate(-1)

  const handleScroll = throttle(() => {
    if (
      Math.abs(window.innerHeight + document.documentElement.scrollTop - document.documentElement.offsetHeight) >
      SCROLLING_HEIGHT_ABS_ERROR
    ) {
      return
    }

    setOffset((pre) => pre + LIMIT)
  }, SCROLLING_THROTTLING_DURATION)

  const handleOpenChat = () => {
    setOpenChat(true)
    dispatch(setEmptyChannelList(false))
    dispatch(setPartnerChatIDState([playerId]))
  }

  useEffect(() => {
    setOffset(OFFSET)
    setMatches([])
  }, [eventFormat, sortParameter, startDate, endDate, debouncedEventName])

  useEffect(() => {
    if (user) onGetMatchesHistory()
  }, [offset, user, eventFormat, sortParameter, startDate, endDate, debouncedEventName])

  useEffect(() => {
    if (user) {
      onGetMatchesHistory()
      setMatches([])
    }
  }, [playerId, user])

  useEffect(() => {
    if (resMatchesHistory) setMatches([...matches, ...resMatchesHistory.hits])
    if (resMatchesHistory?.hasMore) window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [resMatchesHistory])

  useEffect(() => {
    if (user && !user?.isValidEmail) dispatch(setVerifyEmailModalState(true))
  }, [user])

  return (
    <>
      {!isShowDropdown && <MobileSidebar />}
      <MainLayout>
        <MainSidebar />
        <MainContent>
          <div className={`flex flex-col mb-20 gap-y-6 lg:mb-0 ${showLimitBanner ? 'pb-28' : ''}`}>
            <div className="flex items-center">
              <Button onClick={onGoBack} className="hover:opacity-50">
                <ArrowBack className="text-white" />
              </Button>
              <h1 className="ml-3 text-sm md:text-xl text-white font-semibold">{t('player.playerProfile')}</h1>
            </div>

            {requiresUnlock ? (
              <ProfileUnlockGate onUnlock={() => player && recordView(player.id)} />
            ) : (
              <>
                {player && resFollowingInfo ? (
                  <Profile
                    playerId={playerId}
                    data={player}
                    openChat={handleOpenChat}
                    followingInfo={resFollowingInfo?.result}
                  />
                ) : (
                  <ProfileLoading />
                )}

                {user && user.isValidEmail && (
                  <div className="flex w-full md:gap-x-6 xl:gap-x-16 gap-y-8 flex-col md:flex-row pb-6 justify-start">
                    <Statistical
                      userId={player?.id as number}
                      statsDoubles={player?.ratings?.doubles as string}
                      statsSingles={player?.ratings?.singles as string}
                      statsSinglesReliability={player?.ratings?.singlesReliabilityScore as string}
                      statsDoublesReliability={player?.ratings?.doublesReliabilityScore as string}
                      statsProvisional={player?.ratings?.provisionalRatings}
                    />
                    <Matches
                      isLoading={isLoadingMatches}
                      matches={matches}
                      playerId={player?.id as number}
                      eventFormat={eventFormat}
                      setEventFormat={setEventFormat}
                      sortParameter={sortParameter}
                      setSortParameter={setSortParameter}
                      setMatches={setMatches}
                      setOffset={setOffset}
                      startDate={startDate}
                      endDate={endDate}
                      setStartDate={setStartDate}
                      setEndDate={setEndDate}
                      searchText={searchEventName}
                      setSearchText={setSearchEventName}
                      isEmpty={
                        resMatchesHistory?.empty &&
                        !eventFormat &&
                        sortParameter === 'MATCH_DATE' &&
                        !startDate &&
                        !endDate &&
                        !searchEventName
                      }
                    />
                  </div>
                )}

                {/* Not Login */}
                {!user && (
                  <div className="flex w-full justify-center">
                    <div className="w-full sm:w-2/3 flex flex-col items-center gap-y-4">
                      <h2 className="text-xl font-semibold text-white">{t('player.signUpToSeeInfo')}</h2>
                      <p className="text-white text-base font-medium text-opacity-70">{t('player.loginToSeeInfo')}</p>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </MainContent>
      </MainLayout>
      {showLimitBanner && <ProfileLimitBanner key={playerId} />}
      {openChat && <ChatModal openChat={openChat} closeModal={closeModal} setOpenChat={setOpenChat} enabled={true} />}
      {player?.sponsor && (
        <SponsoredModal open={openSponsored} onClose={toggleSponsoredModal} sponsor={player?.sponsor} />
      )}
    </>
  )
}
